/**
 * useTcaAutoLogin — resilient TCA auto-login for managed Windows devices.
 *
 * Replaces the old one-shot probe on the login page. Keeps retrying while
 * `enabled` is set so auto-login works when:
 *   - the agent service starts after the browser tab (slow boot)
 *   - the screen is locked when the page loads (retries on os_unlocked)
 *   - the Windows session switches to the managed account mid-flight
 *   - the portal session expired while the tab was already open
 *
 * Triggers:
 *   - periodic retry on a backoff cadence
 *   - immediate retry on bridge SSE os_unlocked / os_logon events
 *   - visibility regain (tab refocused)
 *
 * `skip` latches auto-login off for this page-load (e.g. right after an
 * explicit sign-out or an admin session termination — silently re-logging
 * in would defeat both).
 *
 * Manual login remains available throughout — the hook never blocks the
 * form; it only reports status so the caller can explain WHY auto-login
 * didn't fire (wrong Windows account, locked screen, agent absent).
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { isTauriDesktop } from '@/lib/desktop/desktopUtils';
import {
  checkAutoLoginEligibility,
  getAutoLoginSignature,
  attemptTcaAutoLogin,
  type TcaAutoLoginResult,
} from '@/lib/tcaBridge';

const BRIDGE_SSE_URL = 'http://127.0.0.1:9797/api/session-events';
const BRIDGE_SEEN_KEY = 'tca-bridge-seen';

const RETRY_DELAY_MS = 15_000;        // steady cadence while waiting
const FAST_RETRY_MS = 2_500;          // right after unlock/logon/visible
const SLOW_RETRY_MS = 60_000;         // non-actionable reasons (not enrolled)
const COLD_RETRY_MS = 300_000;        // browsers that have never seen a bridge — the vast majority never have a TCD installed
const MIN_ATTEMPT_GAP_MS = 10_000;    // portal-attempt rate limit

function readBridgeSeen(): boolean {
  try {
    return typeof window !== 'undefined' && localStorage.getItem(BRIDGE_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

export type TcaAutoLoginPhase =
  | 'idle'
  | 'probing'
  | 'signing_in'
  | 'success'
  | 'wrong_account'
  | 'no_binding'
  | 'screen_locked'
  | 'bridge_unavailable'
  | 'not_enrolled'
  | 'ineligible'
  | 'failed';

export interface TcaAutoLoginState {
  phase: TcaAutoLoginPhase;
  /** Raw bridge reason string, when present. */
  reason?: string;
  /** Windows account currently signed in on the console. */
  consoleUser?: string;
  /** Managed usernames on this device (accounts the agent created). */
  managedUsernames: string[];
  /** Last portal-side error message (phase === 'failed'). */
  error?: string;
  /** True once at least one probe completed. */
  probed: boolean;
}

export interface UseTcaAutoLoginOptions {
  /** Only attempt while true (typically: not authenticated). */
  enabled: boolean;
  /** Latch off for this page-load (post sign-out / session_terminated). */
  skip?: boolean;
  onSuccess: (data: NonNullable<TcaAutoLoginResult['data']>) => void | Promise<void>;
}

function isBrowserOnWindows(): boolean {
  return typeof navigator !== 'undefined' && /Windows/i.test(navigator.userAgent);
}

export function useTcaAutoLogin({
  enabled,
  skip = false,
  onSuccess,
}: UseTcaAutoLoginOptions): TcaAutoLoginState {
  const [state, setState] = useState<TcaAutoLoginState>({
    phase: 'idle',
    managedUsernames: [],
    probed: false,
  });

  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const lastAttemptAtRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  // Seed from the persisted flag — a browser that has seen a bridge before
  // keeps the responsive cadence; one that hasn't probes once per page
  // load (fresh-install detection) then drops to the cold cadence.
  const bridgeSeenRef = useRef(readBridgeSeen());
  const doneRef = useRef(false);
  // Mirrors state.phase so the retry loop can read the latest phase
  // without scheduling inside a setState updater (React double-invokes
  // updaters in dev — a schedule() side effect there multiplies timers).
  const lastPhaseRef = useRef<TcaAutoLoginPhase>('idle');
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const setPhase = useCallback((patch: Partial<TcaAutoLoginState>) => {
    if (!mountedRef.current) return;
    if (patch.phase) lastPhaseRef.current = patch.phase;
    setState((prev) => {
      // Skip the re-render when the probe resolved to the same state —
      // every bridge_unavailable cycle would otherwise flicker the page.
      const next = { ...prev, ...patch, probed: true };
      if (
        prev.probed === next.probed &&
        prev.phase === next.phase &&
        prev.reason === next.reason &&
        prev.consoleUser === next.consoleUser &&
        prev.error === next.error &&
        prev.managedUsernames.length === next.managedUsernames.length &&
        prev.managedUsernames.every((u, i) => u === next.managedUsernames[i])
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const runAttempt = useCallback(async () => {
    if (!mountedRef.current || inFlightRef.current || doneRef.current) return;
    inFlightRef.current = true;
    // Only surface 'probing' before the first result — background retries
    // flipping phase probing → X every cycle would flicker the UI.
    if (lastPhaseRef.current === 'idle') {
      setPhase({ phase: 'probing' });
    }
    try {
      const eligibility = await checkAutoLoginEligibility();
      if (!mountedRef.current || doneRef.current) return;

      if (!eligibility) {
        // Bridge unreachable — agent absent, starting, or restarting.
        setPhase({ phase: 'bridge_unavailable', reason: undefined });
        return;
      }
      bridgeSeenRef.current = true;
      try { localStorage.setItem(BRIDGE_SEEN_KEY, '1'); } catch { /* private mode */ }

      const managed = eligibility.managed_usernames ?? [];
      if (!eligibility.eligible) {
        const reason = eligibility.reason || 'unknown';
        const phase: TcaAutoLoginPhase =
          reason === 'wrong_account' ? 'wrong_account'
            : reason === 'no_binding' ? 'no_binding'
              : reason === 'screen_locked' ? 'screen_locked'
                : reason === 'device_not_enrolled' ? 'not_enrolled'
                  : 'ineligible';
        setPhase({
          phase,
          reason,
          consoleUser: eligibility.username,
          managedUsernames: managed,
        });
        return;
      }

      // Eligible — rate-limit actual portal attempts so repeated wakeups
      // (unlock + visibility + poll landing together) don't spam nonces.
      const now = Date.now();
      if (now - lastAttemptAtRef.current < MIN_ATTEMPT_GAP_MS) {
        setPhase({ phase: 'probing' });
        return;
      }
      lastAttemptAtRef.current = now;

      const signature = await getAutoLoginSignature();
      if (!mountedRef.current || doneRef.current) return;
      if (!signature) {
        setPhase({ phase: 'failed', error: 'Could not get auto-login signature from the agent' });
        return;
      }

      setPhase({ phase: 'signing_in' });
      const result = await attemptTcaAutoLogin(eligibility, signature);
      if (!mountedRef.current || doneRef.current) return;

      if (result.success && result.data) {
        doneRef.current = true;
        setPhase({ phase: 'success' });
        await onSuccessRef.current(result.data);
      } else {
        setPhase({
          phase: 'failed',
          error: result.error || 'Auto-login failed',
          consoleUser: eligibility.username,
          managedUsernames: managed,
        });
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [setPhase]);

  // Self-scheduling retry loop — cadence depends on why the last probe
  // didn't complete. Runs only while enabled && !skip && !done.
  useEffect(() => {
    if (!enabled || skip) return;
    if (typeof window === 'undefined') return;
    if (isTauriDesktop() || Capacitor.isNativePlatform() || !isBrowserOnWindows()) return;

    mountedRef.current = true;

    // Function declarations hoist — tick/schedule reference each other.
    function schedule(delay: number) {
      if (!mountedRef.current || doneRef.current) return;
      // One timer chain at a time — a stray pending timer would fork the
      // loop and double the probe rate from here on.
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        void tick();
      }, delay);
    }

    async function tick() {
      if (!mountedRef.current || doneRef.current) return;
      await runAttempt();
      if (!mountedRef.current || doneRef.current) return;
      const phase = lastPhaseRef.current;
      const delay =
        phase === 'bridge_unavailable'
          ? (bridgeSeenRef.current ? SLOW_RETRY_MS : COLD_RETRY_MS)
          : phase === 'not_enrolled' || phase === 'ineligible' || phase === 'no_binding'
            ? SLOW_RETRY_MS
            : RETRY_DELAY_MS;
      schedule(delay);
    }

    void tick();

    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, skip]);

  // Bridge SSE — os_unlocked / os_logon are the exact moments a pending
  // attempt becomes viable. Only opened after the bridge has answered a
  // probe: a refused EventSource auto-reconnects forever and spams
  // ERR_CONNECTION_REFUSED for every retry. A dropped connection closes
  // rather than reconnect-looping; the poll cadence reopens it after the
  // next successful probe.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!enabled || skip || isTauriDesktop() || Capacitor.isNativePlatform() || !isBrowserOnWindows()) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      return;
    }

    if (!bridgeSeenRef.current || state.phase === 'bridge_unavailable') {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      return;
    }
    if (eventSourceRef.current) return;

    const wake = () => {
      if (!mountedRef.current || doneRef.current) return;
      // Give WTS a beat to settle before querying console state.
      setTimeout(() => { void runAttempt(); }, FAST_RETRY_MS);
    };

    let es: EventSource | null = null;
    try {
      es = new EventSource(BRIDGE_SSE_URL, { withCredentials: false });
      eventSourceRef.current = es;
      es.onmessage = (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data) as { type?: string };
          if (data.type === 'os_unlocked' || data.type === 'os_logon') {
            wake();
          }
        } catch {
          // malformed event — ignore
        }
      };
      es.onerror = () => {
        // Close rather than letting EventSource reconnect-loop against a
        // dead bridge — the poll loop reopens it when probes succeed again.
        es?.close();
        if (eventSourceRef.current === es) eventSourceRef.current = null;
      };
    } catch {
      // EventSource construction failed — poll loop covers us.
    }

    // Tab regained focus — user may have switched Windows accounts or the
    // agent may have started while the tab was backgrounded.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') wake();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, skip, state.phase, runAttempt]);

  useEffect(() => () => {
    mountedRef.current = false;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  return state;
}
